// Shorthand expiry entry — lets a user type a token like "0DTE", "M1", "W2",
// "Q1", "3D", "2W", "1M" into the expiry field instead of only picking a date
// off the calendar. Every resolved date must land exactly on a real listed
// expiry for the instrument's token — if it doesn't, the input is rejected,
// since an expiry off the real chain isn't tradable anyway.
//
// Two families, distinguished by argument order:
//   - Ordinal lookups (LETTER first: 0DTE, M<n>, W<n>, Q<n>) — index into the
//     real listed-expiry chain, classified into daily/weekly/monthly/quarterly.
//   - Day-count arithmetic (NUMBER first: <n>D, <n>W, <n>M) — compute a
//     candidate date from today, then require it to exactly match a listed
//     expiry.
//
// Classification (matches Deribit's real listing convention, confirmed
// against a live expiry chain):
//   - weekly    = every Friday in the chain (superset)
//   - monthly   = the Fridays that are the LAST Friday of their month
//                 (a strict subset of weekly)
//   - quarterly = the monthly Fridays that fall in a quarter-end month
//                 (Mar/Jun/Sep/Dec) — a strict subset of monthly
//   - daily     = anything that isn't a Friday
// A given date can satisfy more than one bucket at once (e.g. the last
// Friday of September is simultaneously weekly/monthly/quarterly) — that's
// expected, not a bug.

export type ExpiryBucket = 'daily' | 'weekly' | 'monthly' | 'quarterly';

const FRIDAY = 5;
const QUARTER_END_MONTHS = [2, 5, 8, 11]; // Mar/Jun/Sep/Dec, 0-indexed

// Normalise any Date (typically `new Date()`, local wall-clock time) to a
// UTC-midnight Date representing the same calendar day. All arithmetic below
// operates in this UTC-anchored space, matching how listed expiries are
// parsed from bare "YYYY-MM-DD" strings.
export const toUtcMidnight = (d: Date): Date =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

const parseIsoDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const isFriday = (d: Date): boolean => d.getUTCDay() === FRIDAY;

const isLastFridayOfMonth = (d: Date): boolean => {
  const next = new Date(d.getTime());
  next.setUTCDate(next.getUTCDate() + 7);
  return next.getUTCMonth() !== d.getUTCMonth();
};

const isQuarterEndMonth = (d: Date): boolean => QUARTER_END_MONTHS.includes(d.getUTCMonth());

// Classify the real listed-expiry chain (ISO "YYYY-MM-DD" strings) into the
// four buckets, keeping only expiries on/after `today` and sorted ascending.
export function classifyExpiries(expiries: string[], today: Date): Record<ExpiryBucket, string[]> {
  const anchor = toUtcMidnight(today).getTime();
  const sorted = [...new Set(expiries)].filter((e) => parseIsoDate(e).getTime() >= anchor).sort();

  const buckets: Record<ExpiryBucket, string[]> = {
    daily: [],
    weekly: [],
    monthly: [],
    quarterly: [],
  };

  for (const e of sorted) {
    const d = parseIsoDate(e);
    if (!isFriday(d)) {
      buckets.daily.push(e);
      continue;
    }
    buckets.weekly.push(e);
    if (isLastFridayOfMonth(d)) {
      buckets.monthly.push(e);
      if (isQuarterEndMonth(d)) {
        buckets.quarterly.push(e);
      }
    }
  }

  return buckets;
}

// Add N calendar months to `date` (expected UTC-midnight-anchored), clamping
// to the last day of the target month when the original day-of-month doesn't
// exist there (e.g. Jan 31 + 1M -> Feb 28/29, not Mar 3).
export function addCalendarMonths(date: Date, months: number): Date {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const firstOfTarget = new Date(Date.UTC(y, m + months, 1));
  const daysInTargetMonth = new Date(
    Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const clampedDay = Math.min(d, daysInTargetMonth);
  return new Date(
    Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth(), clampedDay),
  );
}

// Find the listed expiry closest to `candidate` (future-filtered, per
// `today`). Ties (equal distance on both sides) prefer the earlier date.
// Used by the day-count arithmetic tokens (<n>D/<n>W/<n>M) — today's
// day-of-week rarely lines up exactly with a listed Friday expiry, so
// snapping to the nearest real date (rather than requiring an exact
// match) is what makes these actually useful day-to-day.
function nearestExpiry(candidate: Date, today: Date, listedExpiries: string[]): string | null {
  const anchor = toUtcMidnight(today).getTime();
  const future = [...new Set(listedExpiries)].filter((e) => parseIsoDate(e).getTime() >= anchor);
  if (future.length === 0) return null;

  const target = candidate.getTime();
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const e of future) {
    const diff = Math.abs(parseIsoDate(e).getTime() - target);
    if (diff < bestDiff || (diff === bestDiff && best !== null && e < best)) {
      best = e;
      bestDiff = diff;
    }
  }
  return best;
}

// Resolve a shorthand token against a token's real listed-expiry chain.
// Returns an ISO "YYYY-MM-DD" string on success, or null if the shorthand is
// malformed, out of range, or the chain is empty. Day-count arithmetic
// tokens (<n>D/<n>W/<n>M) snap to the nearest listed expiry rather than
// requiring an exact match — see nearestExpiry above.
export function resolveExpiryShorthand(
  raw: string,
  today: Date,
  listedExpiries: string[],
): string | null {
  const token = raw.trim().toUpperCase();
  if (!token) return null;

  const anchor = toUtcMidnight(today);
  const buckets = classifyExpiries(listedExpiries, anchor);

  if (token === '0DTE') {
    const all = [...buckets.daily, ...buckets.weekly].sort();
    return all[0] ?? null;
  }

  const ordinal = token.match(/^([MWQ])(\d+)$/);
  if (ordinal) {
    const [, letter, nStr] = ordinal;
    const n = Number(nStr);
    if (n < 1) return null;
    const bucket =
      letter === 'M' ? buckets.monthly : letter === 'W' ? buckets.weekly : buckets.quarterly;
    return bucket[n - 1] ?? null;
  }

  const arithmetic = token.match(/^(\d+)([DWM])$/);
  if (arithmetic) {
    const [, nStr, unit] = arithmetic;
    const n = Number(nStr);
    if (n < 1) return null;

    let candidate: Date;
    if (unit === 'D') {
      candidate = new Date(anchor.getTime());
      candidate.setUTCDate(candidate.getUTCDate() + n);
    } else if (unit === 'W') {
      candidate = new Date(anchor.getTime());
      candidate.setUTCDate(candidate.getUTCDate() + n * 7);
    } else {
      candidate = addCalendarMonths(anchor, n);
    }

    return nearestExpiry(candidate, today, listedExpiries);
  }

  return null;
}
